import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { scaleLinear, type AxisTick } from '@groove/core';

/**
 * Chart tone, per docs/groove/standards/data-viz.md: `default` →
 * `--groove-color-data-viz-1`, `muted` → `--groove-color-data-viz-2`,
 * `accent` → `--groove-color-accent-solid`.
 *
 * Deliberately declared here rather than as a `*.axes.ts` vocabulary in
 * @groove/core, matching `[grooveSparkline]`. Those files exist to feed the
 * coverage matrix's cell product, and the chart family is recorded as outside
 * that matrix — see `COVERAGE_EXCLUDED_FAMILIES` in @groove/core's
 * `lib/manifest/guard.ts`. Publishing an axes file for a family the guard is
 * told to ignore would read as an entry someone forgot to register.
 *
 * Tone governs the COLOUR SOURCE only. Stroke weight is a separate concern and
 * is expressed as the `--_chart-stroke` custom property in chart.css, so a
 * consumer can de-emphasize a line's weight without changing its hue, or the
 * reverse.
 */
export type ChartTone = 'default' | 'muted' | 'accent';

/**
 * How a series is drawn, and — per the data-viz visual integrity contract — what
 * that drawing CLAIMS about the data. `solid` is observed data; `dashed` is
 * data that is not observed (estimated, interpolated, or projected). The
 * distinction is carried by line style rather than by colour so it survives
 * forced-colors and colour vision deficiency.
 */
export type ChartDash = 'solid' | 'dashed';

export interface ChartPoint {
  x: number;
  y: number;
}

/**
 * A single called-out point on a series, drawn as a dot with an optional label.
 *
 * Both coordinates are REQUIRED and in domain units. An earlier version of this
 * component took only `x` and hardwired the dot to the zero baseline, which
 * silently encoded one consumer's meaning ("the value reaches zero here") into
 * the geometry and left every other consumer with a marker that could not be
 * positioned.
 */
export interface ChartMarker {
  x: number;
  y: number;
  label?: string;
}

export interface ChartSeriesInput {
  id: string;
  label: string;
  tone: ChartTone;
  dash: ChartDash;
  points: readonly ChartPoint[];
  marker: ChartMarker | null;
}

export interface ChartInput {
  ariaLabel: string;
  series: readonly ChartSeriesInput[];
  xTicks: readonly AxisTick[];
  yTicks: readonly AxisTick[];
  /**
   * Domain x-position of an optional vertical reference rule — a "you are
   * here" line: the present moment, a threshold, a release boundary. `null`
   * draws no rule.
   */
  referenceX: number | null;
  tableCaption: string;
  tableColumns: readonly string[];
  tableRows: readonly (readonly string[])[];
}

const VB_W = 320;
const VB_H = 180;
const GUTTER_L = 40;
const GUTTER_B = 24;
const GUTTER_T = 12;
const GUTTER_R = 64; // room for the direct end-of-line labels

/**
 * Minimum vertical gap (SVG user units) enforced between two series' direct
 * end-of-line labels.
 *
 * JUDGMENT, not a standard: ~1.3x the label font size, chosen because that is
 * enough to keep glyphs from overlapping without spreading converging series
 * unnecessarily far apart. Series that converge to the same final value would
 * otherwise stack their labels at an identical coordinate.
 */
const MIN_LABEL_GAP = 9;

/**
 * De-collides a set of raw label-Y positions so no two are closer than
 * {@link MIN_LABEL_GAP}, without changing their relative order. Pure
 * geometry — does not touch the line/marker positions, only where the text
 * labels land.
 *
 * Bounds guarantee, and its LIMIT — this is a real constraint on the component,
 * not a note about one caller. When the labels *fit* — i.e.
 * `(n-1) * MIN_LABEL_GAP <= maxY - minY` — every result stays within
 * `[minY, maxY]` AND is at least the min gap from its neighbours. The plot is
 * ~144 user units tall, so the condition holds for up to 17 series. Beyond
 * that the two clamp passes cannot satisfy both bounds and the gap at once;
 * order and separation are preserved best-effort but a label may fall outside
 * `[minY, maxY]`. A chart needing more series than that should distribute
 * labels evenly across the range instead, or move to an adjacent legend.
 *
 * Algorithm: sort by raw Y, then walk forward pushing any label that's
 * closer than the min gap to its predecessor further down; if that pushes
 * the last label past `maxY`, clamp it there and walk backward re-enforcing
 * the gap upward; finally do the symmetric clamp+walk for `minY`.
 * Deterministic: same inputs always produce the same outputs.
 */
function dodgeLabelYs(
  rawYs: readonly number[],
  minY: number,
  maxY: number,
): number[] {
  const n = rawYs.length;
  if (n === 0) {
    return [];
  }

  const order = rawYs
    .map((_, i) => i)
    .sort((a, b) => (rawYs[a] ?? 0) - (rawYs[b] ?? 0) || a - b);
  const sortedYs = order.map((i) => rawYs[i] ?? 0);

  const enforceForward = (from: number): void => {
    for (let i = from; i < n; i++) {
      const prev = sortedYs[i - 1] ?? 0;
      const cur = sortedYs[i] ?? 0;
      if (cur - prev < MIN_LABEL_GAP) {
        sortedYs[i] = prev + MIN_LABEL_GAP;
      }
    }
  };
  const enforceBackward = (from: number): void => {
    for (let i = from; i >= 0; i--) {
      const next = sortedYs[i + 1] ?? 0;
      const cur = sortedYs[i] ?? 0;
      if (next - cur < MIN_LABEL_GAP) {
        sortedYs[i] = next - MIN_LABEL_GAP;
      }
    }
  };

  enforceForward(1);

  const lastY = sortedYs[n - 1] ?? 0;
  if (lastY > maxY) {
    sortedYs[n - 1] = maxY;
    enforceBackward(n - 2);
  }

  const firstY = sortedYs[0] ?? 0;
  if (firstY < minY) {
    sortedYs[0] = minY;
    enforceForward(1);
  }

  const result = new Array<number>(n).fill(0);
  order.forEach((originalIndex, sortedPos) => {
    result[originalIndex] = sortedYs[sortedPos] ?? 0;
  });
  return result;
}

interface AxisLayoutTick {
  pos: number;
  label: string;
  y: number;
}
interface AxisLayoutXTick {
  pos: number;
  label: string;
  x: number;
}
interface RenderedSeries {
  id: string;
  label: string;
  /** `null` for the default tone, so the DOM carries no default-value attrs. */
  toneAttr: ChartTone | null;
  dash: ChartDash;
  points: string; // "x,y x,y ..." for <polyline>
  labelX: number;
  labelY: number;
  marker: { cx: number; cy: number; label?: string } | null;
}

/**
 * `<groove-chart>` — a zero-dependency multi-series SVG line chart with axes,
 * direct end-of-line labels, point markers, an optional vertical reference
 * rule, and a visually-hidden data table for the eyes-free path.
 *
 * GEOMETRY-ONLY INPUTS. Every number this component receives is in domain
 * units and every displayed string arrives pre-formatted from the consumer. It
 * formats nothing itself, which is what keeps money, dates, units and locale
 * out of the design system: a chart that formatted values would impose one
 * domain's conventions on every consumer of it.
 *
 * Tokened and forced-colors-aware. Series distinction is carried by dash plus a
 * direct end-of-line label, never colour alone, so two series stay tellable
 * apart when the OS palette collapses every hue to `CanvasText`.
 *
 * CARRIES NO STYLES OF ITS OWN, in keeping with the rest of @groove/angular:
 * every visual lives in `@groove/styles` (`components/chart.css`), which is a
 * PEER dependency. The visually-hidden table uses `.groove-visually-hidden`
 * from that package's utilities layer.
 */
@Component({
  selector: 'groove-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'groove-chart' },
  template: `
    <svg
      class="groove-chart-svg"
      [attr.viewBox]="viewBox"
      role="img"
      [attr.aria-label]="data().ariaLabel"
      preserveAspectRatio="xMidYMid meet"
    >
      <!-- y gridlines + labels -->
      @for (t of yLayout(); track t.pos) {
        <line
          class="groove-chart-gridline"
          [attr.x1]="plotLeft"
          [attr.x2]="plotRight"
          [attr.y1]="t.y"
          [attr.y2]="t.y"
        />
        <text
          class="groove-chart-axis-label"
          [attr.x]="plotLeft - 4"
          [attr.y]="t.y + 3"
          text-anchor="end"
        >
          {{ t.label }}
        </text>
      }
      <!-- x ticks + labels -->
      @for (t of xLayout(); track t.pos) {
        <text
          class="groove-chart-axis-label"
          [attr.x]="t.x"
          [attr.y]="plotBottom + 14"
          text-anchor="middle"
        >
          {{ t.label }}
        </text>
      }
      <!-- vertical reference rule -->
      @if (referenceLine(); as rx) {
        <line
          class="groove-chart-reference-line"
          [attr.x1]="rx"
          [attr.x2]="rx"
          [attr.y1]="plotTop"
          [attr.y2]="plotBottom"
        />
      }
      <!-- series -->
      @for (s of rendered(); track s.id) {
        <!--
          One <g> per series carries the tone. The colour custom property is
          set on the group and INHERITED by the line, the marker and the marker
          label, so a marker can never be painted in a different series' colour
          — which is exactly the defect this replaces, where every marker was
          filled with data-viz-1 regardless of its series.
        -->
        <g class="groove-chart-series" [attr.data-tone]="s.toneAttr">
          <polyline
            class="groove-chart-line"
            [attr.data-dash]="s.dash"
            [attr.points]="s.points"
            fill="none"
          />
          @if (s.marker; as m) {
            <circle
              class="groove-chart-marker"
              [attr.cx]="m.cx"
              [attr.cy]="m.cy"
            />
            @if (m.label; as label) {
              <text
                class="groove-chart-marker-label"
                [attr.x]="m.cx"
                [attr.y]="m.cy - 5"
                text-anchor="middle"
              >
                {{ label }}
              </text>
            }
          }
          <text
            class="groove-chart-series-label"
            [attr.x]="s.labelX"
            [attr.y]="s.labelY"
          >
            {{ s.label }}
          </text>
        </g>
      }
    </svg>
    <table class="groove-chart-table groove-visually-hidden">
      <caption>
        {{
          data().tableCaption
        }}
      </caption>
      <thead>
        <tr>
          @for (col of data().tableColumns; track col) {
            <th scope="col">{{ col }}</th>
          }
        </tr>
      </thead>
      <tbody>
        @for (row of data().tableRows; track $index) {
          <tr>
            @for (cell of row; track $index) {
              @if ($index === 0) {
                <th scope="row">{{ cell }}</th>
              } @else {
                <td>{{ cell }}</td>
              }
            }
          </tr>
        }
      </tbody>
    </table>
  `,
})
export class GrooveChartComponent {
  readonly data = input.required<ChartInput>();

  protected readonly viewBox = `0 0 ${VB_W} ${VB_H}`;
  protected readonly plotLeft = GUTTER_L;
  protected readonly plotRight = VB_W - GUTTER_R;
  protected readonly plotTop = GUTTER_T;
  protected readonly plotBottom = VB_H - GUTTER_B;

  private readonly xDomain = computed<[number, number]>(() => {
    const d = this.data();
    const xs = [
      ...d.xTicks.map((t) => t.pos),
      ...d.series.flatMap((s) => s.points.map((p) => p.x)),
    ];
    return [Math.min(0, ...xs), Math.max(1, ...xs)];
  });

  private readonly yDomain = computed<[number, number]>(() => {
    const d = this.data();
    const ys = [
      ...d.yTicks.map((t) => t.pos),
      ...d.series.flatMap((s) => s.points.map((p) => p.y)),
    ];
    return [0, Math.max(1, ...ys)];
  });

  private sx(x: number): number {
    const [lo, hi] = this.xDomain();
    return scaleLinear(x, lo, hi, this.plotLeft, this.plotRight);
  }

  private sy(y: number): number {
    const [lo, hi] = this.yDomain();
    return scaleLinear(y, lo, hi, this.plotBottom, this.plotTop);
  }

  protected readonly yLayout = computed<AxisLayoutTick[]>(() =>
    this.data().yTicks.map((t) => ({
      pos: t.pos,
      label: t.label,
      y: this.sy(t.pos),
    })),
  );
  protected readonly xLayout = computed<AxisLayoutXTick[]>(() =>
    this.data().xTicks.map((t) => ({
      pos: t.pos,
      label: t.label,
      x: this.sx(t.pos),
    })),
  );
  protected readonly referenceLine = computed<number | null>(() => {
    const rx = this.data().referenceX;
    return rx === null ? null : this.sx(rx);
  });

  protected readonly rendered = computed<RenderedSeries[]>(() => {
    const laidOut = this.data().series.map((s) => {
      const pts = s.points.map((p) => ({ x: this.sx(p.x), y: this.sy(p.y) }));
      // Indexed rather than `pts.at(-1)`: `Array.prototype.at` is ES2022 and
      // this workspace compiles against `lib: ["es2020", "dom"]`.
      const last = pts[pts.length - 1] ?? {
        x: this.plotRight,
        y: this.plotBottom,
      };
      return { s, pts, rawLabelY: last.y + 3 };
    });
    // De-collide the end-of-line labels: two series that converge on the same
    // final value would otherwise land both labels at an identical y.
    const labelYs = dodgeLabelYs(
      laidOut.map((l) => l.rawLabelY),
      this.plotTop,
      this.plotBottom,
    );
    return laidOut.map((l, i) => ({
      id: l.s.id,
      label: l.s.label,
      // Omit the default-value attr to keep the DOM clean — the house style
      // `[grooveSparkline]` sets for its own presentational `tone`.
      toneAttr: l.s.tone === 'default' ? null : l.s.tone,
      // `data-dash` is emitted for BOTH values, including the default, and
      // that asymmetry with `tone` above is deliberate. `tone` is
      // presentational, so its absence legibly means "styled by default".
      // `dash` is a claim ABOUT THE DATA under the data-viz visual integrity
      // contract — solid asserts observed data, dashed asserts data that is
      // not. Encoding "observed" as a MISSING attribute makes the claim
      // indistinguishable from a component that failed to state one, so
      // nothing reading the DOM could tell a rendered assertion from an
      // omission. A positive value is falsifiable; an absence is not.
      dash: l.s.dash,
      points: l.pts.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' '),
      labelX: this.plotRight + 4,
      labelY: labelYs[i] ?? l.rawLabelY,
      marker: l.s.marker
        ? {
            cx: this.sx(l.s.marker.x),
            cy: this.sy(l.s.marker.y),
            ...(l.s.marker.label !== undefined
              ? { label: l.s.marker.label }
              : {}),
          }
        : null,
    }));
  });
}
